import React, { useCallback, useMemo, useState } from 'react';
import { isRelativeURL, noop } from '@splunk/dashboard-utils';
import type {
    CancellableLifecycleEvent,
    LinkToEvent,
    PluginEventHandler,
} from '@splunk/dashboard-types';
import { ProtectedUrlModal } from '../ProtectedUrlModal';

type LinkToUrlPayload = LinkToEvent['payload'];
export type LinkToUrlEvent = CancellableLifecycleEvent &
    Omit<LinkToUrlPayload, 'url'> & {
        url: string;
    };

const isOptionalBoolean = (value: unknown): value is boolean | undefined =>
    value === undefined || typeof value === 'boolean';

const isLinkToUrlEvent = (
    event: Parameters<PluginEventHandler>[0]
): event is LinkToUrlEvent =>
    typeof event.preventDefault === 'function' &&
    typeof event.isCancelled === 'function' &&
    typeof event.url === 'string' &&
    isOptionalBoolean(event.newTab) &&
    isOptionalBoolean(event.isInvalidUrl);

const emptyEventsQueue: LinkToUrlPayload[] = [];

export const useProtectedUrlModal = ({
    isAllowedURL,
    addToAllowedURLs,
}: {
    isAllowedURL: (url: string) => boolean;
    addToAllowedURLs: (url: string) => void;
}) => {
    const [eventQueue, setEventQueue] = useState(emptyEventsQueue);
    const firstEvent = eventQueue.length ? eventQueue[0] : undefined;

    const enqueueEvent = useCallback((event: LinkToUrlPayload) => {
        setEventQueue((queue) => queue.concat(event));
    }, []);

    const dequeueEvent = useCallback(() => {
        setEventQueue((queue) => queue.slice(1));
    }, []);

    const component = useMemo(() => {
        let eventKey = '';
        if (firstEvent) {
            const { url, newTab, isInvalidUrl } = firstEvent;
            eventKey = `${url}-${newTab}-${isInvalidUrl}`;
        }

        return (
            <ProtectedUrlModal
                key={eventKey}
                event={firstEvent}
                onRequestClose={dequeueEvent}
                onAddUrlToAllowList={addToAllowedURLs}
                // required for returning focus to proper element after the modal closes
                returnFocus={noop}
            />
        );
    }, [firstEvent, addToAllowedURLs, dequeueEvent]);

    const onLinkToUrl = useCallback<PluginEventHandler>(
        (event) => {
            if (!isLinkToUrlEvent(event)) {
                return;
            }

            const { url, newTab, isInvalidUrl } = event;
            if (isRelativeURL(url) || isAllowedURL(url)) {
                return;
            }

            // preventDefault needed to cancel the default behavior of opening the link right away
            event.preventDefault();
            enqueueEvent({ url, newTab, isInvalidUrl });
        },
        [enqueueEvent, isAllowedURL]
    );

    return { component, onLinkToUrl };
};
